// Shared API-error copy for Cognitive Flows.
//
// Promoted out of WorkflowEditorPage's local getFriendlyApiError closure so the
// `err.response?.data?.error || 'fallback'` reads scattered across the feature
// stop showing raw server text to customers.
//
// TWO ENTRY POINTS, on purpose:
//   getFriendlyApiError(text)  — you have a STRING. Used for run.message, which
//                                is a 200-body field, not an axios error.
//   apiErrorMessage(err, fb)   — you have an AXIOS ERROR. Layers status/code
//                                rules on top, then delegates to the text rules.
//
// RULE ORDER IS PART OF THE CONTRACT. The first two text rules are the ones
// WorkflowEditorPage shipped with, verbatim and in their original relative
// order (rate-limit before permission). Every rule added since sits BELOW them
// and can therefore only catch strings that previously fell through to raw
// text. Do not reorder; do not widen the top two.

const DEFAULT_MESSAGE = 'Run failed. See details below.';

function normalize(errorText) {
  if (typeof errorText === 'string') return errorText;
  try {
    return JSON.stringify(errorText, null, 2);
  } catch {
    return String(errorText);
  }
}

/**
 * @returns {{ message: string, detail: string, isPermissionError?: boolean }}
 * `detail` is ALWAYS the normalized raw text — the editor's run banner renders
 * it under the friendly headline, and that passthrough is the only way a user
 * (or support) sees what actually happened.
 */
export function getFriendlyApiError(errorText) {
  if (!errorText) return { message: DEFAULT_MESSAGE, detail: '' };
  const detail = normalize(errorText);
  const lower = detail.toLowerCase();

  // ── Rules 1-2: the original WorkflowEditorPage catalogue, unchanged. ──
  if (lower.includes('rate limit') || lower.includes('429')) {
    return {
      message: 'Rate limit exceeded. Please enable billing or slow down your requests.',
      detail,
    };
  }
  if (lower.includes('permission') || lower.includes('does not have permission')) {
    return {
      message: 'Google API permission denied. Check service account access and spreadsheet sharing.',
      detail,
      isPermissionError: true,
    };
  }

  // ── Rules 3+: additive only. Each fires solely on text that used to fall
  //    through to the raw passthrough at the bottom. ──

  // Missing / invalid provider key. Matches the sentences the server's own
  // mappers author (openaiService, geminiService, claudeService). Provider keys
  // are platform-owned (server env), so there is no merchant-side fix to offer.
  if (lower.includes('api key') || lower.includes('invalid_api_key') || lower.includes('unauthorized')) {
    return {
      message: 'AI is temporarily unavailable — the provider rejected our credentials. Please try again shortly or contact support.',
      detail,
    };
  }

  // Quota / credits.
  if (lower.includes('insufficient_quota')
    || lower.includes('quota is exhausted')
    || lower.includes('insufficient credits')
    || lower.includes('not enough credits')
    || lower.includes('exceeded your current quota')) {
    return {
      message: 'Your account is out of credits or provider quota. Top up credits, or add billing at your AI provider, then re-run.',
      detail,
    };
  }

  // Google Sheets / Docs / Slides sharing. Sits below rule 2, so this only
  // catches the "not found because not shared" phrasing, which never says
  // "permission" and today shows as a bare Google API dump.
  // "Unable to parse range" is a missing tab or a malformed range, not a
  // sharing problem — the file opened fine. Say so, or people re-share a sheet
  // that was already shared and the run fails the same way again.
  if (lower.includes('unable to parse range')) {
    return {
      message: "Google opened the file but couldn't find that tab or range. Check the sheet (tab) name and range, then re-run.",
      detail,
    };
  }
  if (lower.includes('requested entity was not found')
    || (lower.includes('spreadsheet') && lower.includes('not found'))
    || lower.includes('caller does not have access')) {
    return {
      message: "Google couldn't open that file. Share it with the connector service account as an Editor, and check the ID and tab name.",
      detail,
      isPermissionError: true,
    };
  }

  // Meta Graph code 1 "reduce the amount of data": the server already retried the
  // fetch at smaller page sizes, so what is left to turn is the report's shape.
  if (lower.includes('reduce the amount of data')) {
    return {
      message: 'Meta rejected this report as too large. Select fewer columns or metrics, add a Status or name filter, or shorten the date range, then re-run.',
      detail,
    };
  }

  // Timeouts.
  if (lower.includes('timeout') || lower.includes('timed out')
    || lower.includes('etimedout') || lower.includes('econnaborted')
    || lower.includes('deadline exceeded')) {
    return {
      message: 'That took too long and was stopped. Reduce the data size or try again in a few minutes.',
      detail,
    };
  }

  // Fall through: authored server text, shown as-is. This passthrough is the
  // reason the server layer must never emit raw runtime text (see
  // server/utils/apiError.js).
  return { message: detail, detail };
}

// Status/code rules — these need the error OBJECT, not just its text, so they
// live in apiErrorMessage and run BEFORE the text rules.
const CODE_MESSAGES = {
  INTERNAL_ERROR: 'Something went wrong on our side. Please try again.',
  WORKSPACE_FORBIDDEN: "You don't have access to this workspace. Ask an account owner to add you.",
  // Retired server-side (deletion now cascades a workspace's contents), kept
  // so a browser talking to an older deployed server still gets a sentence.
  WORKSPACE_NOT_EMPTY: 'This workspace still has content — move or delete its workflows, data sources, knowledge entries and files first.',
  OWNER_ONLY: 'Only the account owner can do this.',
  ACCOUNT_MANAGE_FORBIDDEN: 'Only an account owner or admin can do this.',
  CANNOT_DEMOTE_OWNER: "The account owner's role can't be changed here — transfer ownership instead.",
  CANNOT_EDIT_OWNER: 'The account owner always has full access — transfer ownership to change it.',
  USE_TRANSFER_OWNERSHIP: 'Use Transfer ownership to make someone the account owner.',
  CONFIRM_MISMATCH: "Type the new owner's email address exactly to confirm.",
  ALREADY_OWNER: 'They already own this account.',
  MEMBER_NOT_FOUND: "That person isn't on this account yet. Invite them first.",
  NOT_A_MEMBER: "That person isn't on this account yet. Invite them first.",
  ADS_ACCOUNT_LOCKED: 'This workspace is fixed to one ad account. Change it in Settings → Teams & Workspace.',
  // Platform-owned key now (server env), so there is no merchant-side fix.
  LLM_KEY_MISSING: 'AI is temporarily unavailable on this server. Please try again shortly or contact support.',
  INSUFFICIENT_CREDITS: 'Your account is out of credits. Top up to keep running agents.',
  // Free plans cannot buy credits — the button is hidden, and the server
  // refuses the order. Only a stale tab reaches this.
  UPGRADE_REQUIRED: "Your plan doesn't include credit top-ups. Upgrade to keep running agents.",
};

/** The codes the server refuses a run with when the wallet is empty. */
const CREDIT_EXHAUSTED_CODES = new Set(['INSUFFICIENT_CREDITS', 'UPGRADE_REQUIRED']);

/**
 * Did this failure happen because the account is out of credits?
 *
 * Lives here because this module already owns what an error from our API looks
 * like, and the three surfaces that ask (workflows, canvas, chat) get the
 * answer from ONE place rather than each matching on its own subset.
 *
 * Takes an axios error OR a plain `{ status, code }` — the canvas agent-block
 * run streams over `fetch`, which never produces an axios error, so it hands
 * over the status it read off the Response itself.
 *
 * 402 is included without a code: it is the status canvas and chat refuse with,
 * and it means exactly one thing across this API.
 */
export function isCreditExhaustedError(err) {
  if (!err) return false;
  const status = err.response?.status ?? err.status;
  if (Number(status) === 402) return true;
  const code = err.response?.data?.code ?? err.code;
  return typeof code === 'string' && CREDIT_EXHAUSTED_CODES.has(code);
}

/** The correlation id the server attaches to genericised 5xx responses. */
export function apiErrorRef(err) {
  const ref = err?.response?.data?.ref;
  return typeof ref === 'string' && ref ? ref : null;
}

/**
 * Drop-in for `err.response?.data?.error || 'fallback'`:
 *   toast.error(err.response?.data?.error || 'Failed to copy workflow')
 *   → toast.error(apiErrorMessage(err, 'Failed to copy workflow'))
 */
export function apiErrorMessage(err, fallback = 'Something went wrong. Please try again.') {
  const response = err?.response;

  // No response at all: the request never completed.
  if (!response) {
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    if (offline) return "You're offline. Reconnect and try again.";
    if (err?.code === 'ERR_NETWORK') return "Couldn't reach the server. Check your connection and try again.";
    // Cancellations (unmount, aborted poll) keep the caller's own wording
    // rather than being reported as a failure of their own.
    if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return fallback;
    if (err?.code === 'ECONNABORTED') return 'That request took too long. Please try again.';
    return err?.message || fallback;
  }

  const { status, data } = response;
  // 401 is intercepted in utils/api.js (redirect to /login); this only covers
  // the raw-fetch SSE callers that bypass the interceptor.
  if (status === 401) return 'Your session expired. Please sign in again.';

  const code = typeof data?.code === 'string' ? data.code : null;
  if (code && CODE_MESSAGES[code]) return CODE_MESSAGES[code];
  if (status === 403) return "You don't have permission to do that. Ask an account owner for access.";

  const text = data?.error || data?.message;
  if (!text) {
    return status >= 500 ? CODE_MESSAGES.INTERNAL_ERROR : fallback;
  }
  return getFriendlyApiError(text).message;
}

/** Full banner shape (message + raw detail + isPermissionError) from an axios error. */
export function apiErrorDetails(err, fallback) {
  const text = err?.response?.data?.error || err?.message || fallback;
  const friendly = getFriendlyApiError(text);
  return {
    ...friendly,
    message: apiErrorMessage(err, fallback),
    detail: friendly.detail || text || '',
    ref: apiErrorRef(err),
  };
}
